import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Empty, Avatar, Checkbox, List, Badge, Row, Col, Popconfirm } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  SendOutlined, 
  PictureOutlined, 
  EditOutlined,
  PlaySquareOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import CreatePlaylistModal from './CreatePlaylistModal';

const Playlist = () => {
  const [playlists, setPlaylists] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [boxes, setBoxes] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [selectedBoxIds, setSelectedBoxIds] = useState([]);

  // --- CONFIG: ADJUST CARD HEIGHT HERE ---
  const CARD_MIN_HEIGHT = '40px';
  const CARD_PADDING = '10px 20px';
  // ---------------------------------------

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get('/api/box/playlists/');
      setPlaylists(response.data);
    } catch (error) {
      console.error('Error loading playlists:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get('/api/box/media/');
      setMediaFiles(response.data);
    } catch (error) {
      console.error('Error loading media:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoxes = async () => {
    try {
      const response = await axios.get('/api/box/list/');
      setBoxes(response.data);
    } catch (error) {
      console.error('Error loading boxes:', error);
    }
  };

  useEffect(() => {
    fetchPlaylists();
    fetchMedia();
    fetchBoxes();
  }, []);

  const openEditModal = (playlist) => {
    setEditingPlaylist(playlist);
    const itemsForEditor = (playlist.items || []).map(item => ({
      ...item.media_details,
      id: `${item.media_details.id}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      duration: item.duration || 5
    }));
    setSelectedItems(itemsForEditor);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingPlaylist(null);
    setSelectedItems([]);
  };

  const handleSavePlaylist = async (name, resolution, items) => {
    if (!name.trim()) {
      message.error('Please enter playlist name');
      return;
    }
    setCreating(true);
    try {
      if (editingPlaylist) {
        await axios.put(`/api/box/playlists/${editingPlaylist.id}/`, { name, resolution, items });
        message.success('Playlist updated');
      } else {
        await axios.post('/api/box/playlists/', { name, resolution, items });
        message.success('Playlist created');
      }
      closeModal();
      fetchPlaylists();
    } catch (error) {
      message.error('Error saving playlist');
    } finally {
      setCreating(false);
    }
  };

  const handleSendToBox = async () => {
    if (selectedBoxIds.length === 0) {
      message.warning('Select at least one box');
      return;
    }
    setSending(true);
    try {
      await axios.post('/api/box/deploy-playlist/', {
        playlist_id: selectedPlaylistId,
        box_ids: selectedBoxIds
      });
      message.success('Playlist deployed successfully');
      setSendModalVisible(false);
      setSelectedBoxIds([]);
    } catch (error) {
      message.error('Error sending playlist');
    } finally {
      setSending(false);
    }
  };

  const deletePlaylist = async (id) => {
    try {
      await axios.delete(`/api/box/playlists/${id}/`);
      message.success('Playlist deleted');
      if (selectedPlaylistId === id) setSelectedPlaylistId(null);
      fetchPlaylists();
    } catch (error) {
      message.error('Error deleting playlist');
    }
  };

  const columns = [
    {
      title: 'Thumb',
      key: 'thumbnail',
      width: 65,
      render: (record) => {
        const firstItem = record.items && record.items[0];
        const media = firstItem?.media_details;
        if (media?.thumbnail) {
          return (
            <Avatar 
              src={`https://escreen.stand.ledscreen.rs/media/${media.thumbnail}`} 
              shape="square" size="large" style={{ border: '1px solid #334155', cursor: 'pointer' }}
              onClick={() => openEditModal(record)}
            />
          );
        }
        return (
          <Avatar icon={<PictureOutlined />} shape="square" size="large" style={{ background: '#0f172a', border: '1px solid #334155', cursor: 'pointer' }} onClick={() => openEditModal(record)} />
        );
      },
    },
    {
      title: 'Playlist Name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (record) => (
        <span 
          style={{ color: '#3b82f6', fontWeight: '500', cursor: 'pointer' }}
          className="playlist-name-link"
          onClick={() => openEditModal(record)}
        >
          {record.name}
        </span>
      ),
    },
    {
      title: 'Resolution',
      dataIndex: 'resolution',
      key: 'resolution',
      render: (text) => <span style={{ color: '#94a3b8' }}>{text || '-'}</span>,
    },
    {
      title: 'Items',
      key: 'items_count',
      render: (record) => <span style={{ color: '#34d399', fontWeight: 'bold' }}>{record.items?.length || 0} FILES</span>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Space>
          <Button 
            size="small" 
            icon={<SendOutlined />}
            style={selectedPlaylistId === record.id ? { background: '#10b981', borderColor: '#10b981', color: 'white' } : { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}
            onClick={() => setSelectedPlaylistId(record.id === selectedPlaylistId ? null : record.id)}
          >
            {selectedPlaylistId === record.id ? 'Selected' : 'Select'}
          </Button>
          <Button size="small" icon={<EditOutlined />} style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} onClick={() => openEditModal(record)}>Edit</Button>
          <Popconfirm title="Delete this playlist?" onConfirm={() => deletePlaylist(record.id)} okText="Yes" cancelText="No">
            <Button size="small" icon={<DeleteOutlined />} style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={12}>
          <div style={{ 
            background: '#1e293b', 
            padding: CARD_PADDING, 
            borderRadius: '8px', 
            border: '1px solid #334155', 
            borderBottom: '3px solid #3b82f6',
            minHeight: CARD_MIN_HEIGHT,
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Space size="middle">
              <PlaySquareOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL PLAYLISTS</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{playlists.length} Lists</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={12}>
          <div 
            onClick={() => setModalVisible(true)} 
            style={{ 
              background: '#1e293b', 
              padding: CARD_PADDING, 
              borderRadius: '8px', 
              border: '1px solid #334155', 
              borderBottom: '3px solid #3b82f6', 
              cursor: 'pointer',
              minHeight: CARD_MIN_HEIGHT,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Space size="middle">
              <PlusOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>ADD NEW PLAYLIST</div>
                <div style={{ color: 'white', fontSize: '14px' }}>Create media loop</div>
              </div>
            </Space>
            {selectedPlaylistId && (
              <Button 
                type="primary" 
                style={{ background: '#10b981', borderColor: '#10b981' }} 
                icon={<SendOutlined />} 
                onClick={(e) => { e.stopPropagation(); setSendModalVisible(true); }}
              >
                Send to Box
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <div style={{ background: 'rgba(30, 41, 59, 0.3)', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b' }}>
        <Table 
          dataSource={playlists} 
          columns={columns} 
          rowKey="id" 
          loading={loading} 
          pagination={{ pageSize, showSizeChanger: true, onShowSizeChange: (c, size) => setPageSize(size) }} 
          style={{ background: 'transparent' }} 
          locale={{ emptyText: <Empty description={<span style={{ color: '#64748b' }}>No playlists found</span>} /> }} 
        />
      </div>

      <CreatePlaylistModal isVisible={modalVisible} onClose={closeModal} onCreate={handleSavePlaylist} mediaFiles={mediaFiles} selectedItems={selectedItems} setSelectedItems={setSelectedItems} loading={creating} editingPlaylist={editingPlaylist} />

      {sendModalVisible && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-container">
            <div className="custom-modal-header">
              <span className="custom-modal-title">Deploy Playlist to Boxes</span>
              <span className="custom-modal-close" onClick={() => setSendModalVisible(false)}>&times;</span>
            </div>
            <div className="custom-modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <List
                dataSource={boxes}
                renderItem={box => (
                  <List.Item style={{ borderBottom: '1px solid #334155', padding: '12px 0' }}>
                    <Checkbox 
                      style={{ width: '100%', color: 'white' }}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedBoxIds([...selectedBoxIds, box.id]);
                        else setSelectedBoxIds(selectedBoxIds.filter(id => id !== box.id));
                      }}
                      checked={selectedBoxIds.includes(box.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '380px' }}>
                        <span>{box.box_name || box.box_id}</span>
                        <Badge status={box.status === 'online' ? 'success' : 'error'} text={<span style={{ color: '#94a3b8', fontSize: '11px' }}>{box.status}</span>} />
                      </div>
                    </Checkbox>
                  </List.Item>
                )}
              />
            </div>
            <div className="custom-modal-footer">
              <Button onClick={() => setSendModalVisible(false)} className="custom-btn-cancel">Cancel</Button>
              <Button type="primary" onClick={handleSendToBox} loading={sending} className="custom-btn-save">Send</Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ant-table { background: transparent !important; }
        .ant-table-thead > tr > th { background: transparent !important; color: #64748b !important; border-bottom: 1px solid #1e293b !important; text-transform: uppercase; font-size: 11px; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; color: white !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .playlist-name-link:hover { text-decoration: underline !important; }
        .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 1000; }
        .custom-modal-container { background-color: #1e293b; width: 500px; border-radius: 8px; border: 1px solid #334155; box-shadow: 0 0 25px 5px rgba(59, 130, 246, 0.4); overflow: hidden; }
        .custom-modal-header { padding: 16px 24px; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center; }
        .custom-modal-title { color: white; font-size: 16px; font-weight: 600; }
        .custom-modal-close { color: #94a3b8; font-size: 24px; cursor: pointer; line-height: 1; }
        .custom-modal-body { padding: 24px; }
        .custom-modal-footer { padding: 16px 24px; border-top: 1px solid #334155; display: flex; justify-content: flex-end; gap: 12px; }
        .custom-btn-cancel { background: #334155 !important; color: white !important; border: none !important; }
        .custom-btn-save { background: #3b82f6 !important; border: none !important; }
      `}</style>
    </div>
  );
};

export default Playlist;